import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  UserIcon,
  IdentificationIcon,
  BanknotesIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface ApplyFormData {
  // 基本資料
  name: string;
  english_name: string;
  email: string;
  phone: string;
  address: string;
  
  // 身分證件
  id_number: string;
  id_address: string;
  birth_date: string;
  nationality: 'local' | 'non-resident' | 'foreign';
  
  // 銀行資料
  bank_name: string;
  bank_account_name: string;
  bank_branch: string;
  bank_account_number: string;
  
  // 檔案
  id_front_file: File | null;
  id_back_file: File | null;
  bankbook_file: File | null;
}

const PartnerApply: React.FC = () => {
  const navigate = useNavigate();
  const [showIntro, setShowIntro] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState<ApplyFormData>({
    name: '',
    english_name: '',
    email: '',
    phone: '',
    address: '',
    id_number: '',
    id_address: '',
    birth_date: '',
    nationality: 'local',
    bank_name: '',
    bank_account_name: '',
    bank_branch: '',
    bank_account_number: '',
    id_front_file: null,
    id_back_file: null,
    bankbook_file: null,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'id_front_file' | 'id_back_file' | 'bankbook_file') => {
    const file = e.target.files?.[0];
    if (file) {
      // 驗證檔案類型
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('僅支援 JPG、PNG、PDF 格式');
        return;
      }
      
      // 驗證檔案大小（20MB）
      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('檔案大小不能超過 20MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, [field]: file }));
    }
  };

  // 清理 email 用於檔案路徑（將特殊字元替換為安全字元）
  const sanitizeEmailForPath = (email: string): string => {
    return email
      .toLowerCase()
      .trim()
      .replace(/@/g, '_at_')
      .replace(/\./g, '_dot_')
      .replace(/[^a-zA-Z0-9_-]/g, '_');
  };

  // 安全取得檔案副檔名
  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
      // 沒有副檔名或副檔名為空，預設為 jpg
      return 'jpg';
    }
    const extension = filename.substring(lastDotIndex + 1).toLowerCase();
    // 驗證副檔名是否為允許的格式
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
    return allowedExtensions.includes(extension) ? extension : 'jpg';
  };

  // 生成安全的檔案路徑
  const generateFilePath = (email: string, fileType: 'id_front' | 'id_back' | 'bankbook', filename: string): string => {
    const timestamp = Date.now();
    const safeEmail = sanitizeEmailForPath(email);
    const extension = getFileExtension(filename);
    return `partner-applications/${timestamp}_${safeEmail}_${fileType}.${extension}`;
  };

  const uploadFile = async (file: File, path: string): Promise<string> => {
    try {
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('檔案上傳失敗:', error);
      const errorMessage = error instanceof Error ? error.message : '未知錯誤';
      
      // 檢查是否為權限錯誤
      if (errorMessage.includes('unauthorized') || errorMessage.includes('permission')) {
        throw new Error(
          '檔案上傳權限錯誤：請聯繫管理員檢查 Firebase Storage 規則設定。' +
          '錯誤詳情：' + errorMessage
        );
      }
      
      throw new Error(`檔案上傳失敗: ${errorMessage}`);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 驗證必填欄位
    if (!formData.name || !formData.email || !formData.phone) {
      setError('請填寫所有必填欄位');
      return;
    }
    
    if (!formData.id_number || !formData.id_front_file || !formData.id_back_file) {
      setError('請填寫身分證資料並上傳正反面影本');
      return;
    }
    
    if (!formData.bank_name || !formData.bank_account_name || !formData.bank_account_number || !formData.bankbook_file) {
      setError('請填寫銀行資料並上傳存摺影本');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // 上傳檔案 - 使用改進的檔案名稱處理邏輯
      const idFrontUrl = await uploadFile(
        formData.id_front_file!,
        generateFilePath(formData.email, 'id_front', formData.id_front_file!.name)
      );
      
      const idBackUrl = await uploadFile(
        formData.id_back_file!,
        generateFilePath(formData.email, 'id_back', formData.id_back_file!.name)
      );
      
      const bankbookUrl = await uploadFile(
        formData.bankbook_file!,
        generateFilePath(formData.email, 'bankbook', formData.bankbook_file!.name)
      );
      
      // 提交申請資料
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://teleworker-api-jrfj34sryq-de.a.run.app';
      const response = await fetch(`${API_BASE_URL}/api/partners/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          english_name: formData.english_name.trim() || null,
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          address: formData.address.trim() || null,
          id_number: formData.id_number.trim(),
          id_address: formData.id_address.trim() || null,
          birth_date: formData.birth_date || null,
          nationality: formData.nationality,
          bank_name: formData.bank_name.trim(),
          bank_account_name: formData.bank_account_name.trim(),
          bank_branch: formData.bank_branch.trim() || null,
          bank_account_number: formData.bank_account_number.trim(),
          id_front_url: idFrontUrl,
          id_back_url: idBackUrl,
          bankbook_url: bankbookUrl,
          status: 'pending', // 申請狀態
        }),
      });
      
      // 檢查 HTTP 狀態碼
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || data.message || '申請失敗，請稍後再試');
      }
    } catch (err) {
      console.error('申請失敗:', err);
      const errorMessage = err instanceof Error ? err.message : '申請失敗，請稍後再試';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // 介紹彈窗組件
  if (showIntro) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* 標題列 */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="/Logo-with-name.svg" 
                alt="Teleworker" 
                className="h-8 w-auto mr-3"
              />
              <h2 className="text-2xl font-bold text-gray-900">為什麼是 Teleworker</h2>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* 內容區域 */}
          <div className="px-6 py-8 space-y-8">
            {/* 開場 */}
            <div className="space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Teleworker 是一個相信 <span className="font-semibold text-blue-600">「先有生活，工作才會變得亮麗」</span> 的團隊。
              </p>
              <p className="text-gray-700 leading-relaxed">
                我們追求高品質的交付，但不相信燃燒自己是唯一的路。相反地，我們更在意一個人是否能在穩定、清楚、被信任的狀態下，把事情長期做好。
              </p>
            </div>

            {/* 在這裡，你會感受到 */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">在這裡，你會感受到：</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>高度的信任與彈性</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>清楚的期待與邊界</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>對專業、節奏與承諾的尊重</span>
                </li>
              </ul>
            </div>

            {/* 我們如何工作 */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">我們如何工作</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>非同步優先</strong>：所有重要討論與決策，優先留在專案管理系統中，保留脈絡</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>同步有目的</strong>：Slack 用於需要即時對話與釐清的情境</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>尊重狀態與時區</strong>：除非非常緊急，否則不期待即時回應</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span><strong>可以休息，也要說清楚</strong>：我們鼓勵在疲累時主動調整，而不是低效硬撐</span>
                </li>
              </ul>
            </div>

            {/* 自由，來自於責任 */}
            <div className="bg-blue-50 rounded-lg p-6 space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">自由，來自於責任</h3>
              <p className="text-gray-700 leading-relaxed">
                Teleworker 給你高度的自由，但同時也很看重承諾。
              </p>
              <p className="text-gray-700 leading-relaxed">
                承諾不是不能調整，而是在發現風險時要提早說，讓團隊可以一起因應。尊重承諾，代表你尊重的不只是工作，而是每一位夥伴的時間與付出。
              </p>
            </div>

            {/* 關於成長與回饋 */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">關於成長與回饋</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>我們有透明的年度評鑒與薪資調整機制</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>評分不是排名競賽，而是用來對齊合作節奏</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>長期、穩定、高於平均的交付，會被清楚地看見</span>
                </li>
              </ul>
            </div>

            {/* 我們期待這樣的你 */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900">我們期待這樣的你</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>能自我管理時間與節奏</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>願意理解事情的全貌，而不只是被指派任務</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>發現問題時，願意提早溝通，而不是消失或硬撐</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  <span>把工作視為一段長期投入，而不是短期消耗</span>
                </li>
              </ul>
            </div>

            {/* 一點很誠實的說明 */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">一點很誠實的說明</h3>
              <p className="text-gray-700 leading-relaxed">
                Teleworker 在業界的薪資水準相對較高，因此我們會有兩週的磨合期，雙方在這段期間都能確認是否適合合作。若此刻節奏尚未對齊，我們會尊重彼此、暫停合作，也真心期待未來合適的時候再次相遇。
              </p>
            </div>

            {/* 結語 */}
            <div className="text-center py-6 border-t border-gray-200">
              <p className="text-lg text-gray-700 mb-6">
                如果你讀到這裡，感到被理解、被尊重，<br />
                <span className="font-semibold text-blue-600">那我們很期待認識你。</span>
              </p>
              <div className="flex justify-center items-center space-x-2">
                <img 
                  src="/Logo-with-name.svg" 
                  alt="Teleworker" 
                  className="h-6 w-auto"
                />
              </div>
            </div>
          </div>

          {/* 底部按鈕 */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-4">
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              取消
            </button>
            <button
              onClick={() => setShowIntro(false)}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md hover:shadow-lg font-medium"
            >
              我了解了，開始申請
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/Logo-with-name.svg" 
              alt="Teleworker" 
              className="h-12 w-auto"
            />
          </div>
          
          {/* 成功圖示 */}
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-green-100 rounded-full animate-ping"></div>
            <CheckCircleIcon className="relative w-20 h-20 text-green-500 mx-auto" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">申請已提交</h2>
          <p className="text-gray-600 mb-6">
            感謝您的申請！我們已收到您的資料，審核完成後會透過電子郵件通知您。
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            返回登入頁面
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Logo 和標題 */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img 
              src="/Logo-with-name.svg" 
              alt="Teleworker" 
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            承攬夥伴申請
          </h1>
          <p className="text-gray-600">
            請填寫以下資料以申請成為承攬夥伴
          </p>
        </div>

        {/* 優化的進度條 */}
        <div className="mb-8 bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3].map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center flex-1">
                  {/* 步驟圓圈 */}
                  <div className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                    currentStep > step
                      ? 'bg-green-500 border-green-500 text-white shadow-lg'
                      : currentStep === step
                      ? 'bg-blue-600 border-blue-600 text-white shadow-lg scale-110'
                      : 'bg-white border-gray-300 text-gray-400'
                  }`}>
                    {currentStep > step ? (
                      <CheckCircleIcon className="w-7 h-7" />
                    ) : (
                      <span className="font-bold text-lg">{step}</span>
                    )}
                    {/* 當前步驟的脈衝動畫 */}
                    {currentStep === step && (
                      <span className="absolute inset-0 rounded-full bg-blue-600 animate-ping opacity-20"></span>
                    )}
                  </div>
                  {/* 步驟標籤 */}
                  <div className={`mt-3 text-xs font-medium text-center transition-colors duration-300 ${
                    currentStep >= step
                      ? 'text-blue-600'
                      : 'text-gray-400'
                  }`}>
                    {step === 1 && '基本資料'}
                    {step === 2 && '身分證件'}
                    {step === 3 && '銀行資料'}
                  </div>
                </div>
                {/* 連接線 */}
                {index < 2 && (
                  <div className="flex-1 mx-2 h-1 relative -mt-6">
                    <div className={`h-full rounded-full transition-all duration-500 ${
                      currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                    }`} style={{
                      width: currentStep > step ? '100%' : '0%',
                      transition: 'width 0.5s ease-in-out'
                    }}></div>
                    <div className="absolute inset-0 bg-gray-200 rounded-full -z-10"></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          
          {/* 進度百分比 */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-600">進度</span>
              <span className="text-xs font-bold text-blue-600">
                {Math.round((currentStep / 3) * 100)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500 ease-out shadow-sm"
                style={{ width: `${(currentStep / 3) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* 錯誤訊息 */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* 表單 */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl p-6 md:p-8">
          {/* 步驟 1: 基本資料 */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <UserIcon className="w-6 h-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">基本資料</h2>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  中文姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  英文姓名
                </label>
                <input
                  type="text"
                  name="english_name"
                  value={formData.english_name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  電子郵件 <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">（此郵件將作為登入系統的 Google SSO 帳號）</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="example@gmail.com"
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  請使用您要登入系統的 Google 帳號電子郵件
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  電話 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  聯絡地址
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
          )}

          {/* 步驟 2: 身分證件 */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <IdentificationIcon className="w-6 h-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">身分證件</h2>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  身分證字號 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="id_number"
                  required
                  value={formData.id_number}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  身分證地址
                </label>
                <textarea
                  name="id_address"
                  value={formData.id_address}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  出生年月日
                </label>
                <input
                  type="date"
                  name="birth_date"
                  value={formData.birth_date}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  國籍 <span className="text-red-500">*</span>
                </label>
                <select
                  name="nationality"
                  required
                  value={formData.nationality}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="local">本國人</option>
                  <option value="non-resident">非居住者</option>
                  <option value="foreign">外國人</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  身分證正面影本 <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, 'id_front_file')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {formData.id_front_file && (
                  <p className="mt-1 text-sm text-gray-500">
                    已選擇：{formData.id_front_file.name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  身分證背面影本 <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, 'id_back_file')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {formData.id_back_file && (
                  <p className="mt-1 text-sm text-gray-500">
                    已選擇：{formData.id_back_file.name}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* 步驟 3: 銀行資料 */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="flex items-center mb-4">
                <BanknotesIcon className="w-6 h-6 text-blue-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">銀行資料</h2>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  銀行名稱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="bank_name"
                  required
                  value={formData.bank_name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="例如：國泰世華銀行"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  分行名稱
                </label>
                <input
                  type="text"
                  name="bank_branch"
                  value={formData.bank_branch}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="例如：前金分行"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  帳戶姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="bank_account_name"
                  required
                  value={formData.bank_account_name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  銀行帳號 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="bank_account_number"
                  required
                  value={formData.bank_account_number}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  存摺影本 <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, 'bankbook_file')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                {formData.bankbook_file && (
                  <p className="mt-1 text-sm text-gray-500">
                    已選擇：{formData.bankbook_file.name}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  請上傳顯示銀行帳戶資訊的存摺影本
                </p>
              </div>
            </div>
          )}

          {/* 按鈕 */}
          <div className="flex justify-between mt-6">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                上一步
              </button>
            ) : (
              <div></div>
            )}
            
            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                下一步
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    提交中...
                  </>
                ) : (
                  <>
                    <DocumentArrowUpIcon className="w-5 h-5" />
                    提交申請
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* 說明文字 */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>注意事項：</strong>
          </p>
          <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
            <li>請確保所有資料正確無誤</li>
            <li>上傳的檔案格式支援 JPG、PNG、PDF，大小不超過 20MB</li>
            <li>申請提交後，我們會進行審核，審核完成後會透過電子郵件通知您</li>
            <li>如有任何問題，請聯繫我們</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PartnerApply;

